import { Enum } from '@lighthouse/tools'

import type { AlignMent, Direction, Fit, FlexAlign, TextProtocol, VariableADTvalue } from '../protocols'

/** 背景类型 */
export const BACKGROUND_TYPE = Enum('color', 'gradient', 'image', 'variable')
export type BackgroundType = Enum<typeof BACKGROUND_TYPE>

export const GRADIENT_MODE = Enum('linear', 'radial', 'conic')
export type GradientMode = Enum<typeof GRADIENT_MODE>
/** 渐变节点 */
export type GradientStop = {
    color: string
    percentage: number
}
/** 渐变色协议 */
export interface GradientProtocol {
    mode: GradientMode
    angle: number
    width?: number
    height?: number
    stops: GradientStop[]
}

export interface BackgroundColorProtocol {
    color?: string
}
export interface BackgroundGradientProtocol {
    gradient?: GradientProtocol
}
export interface BackgroundImageProtocol {
    image?: {
        resource: VariableADTvalue
        fit: Fit
    }
}
export interface BackgroundVariableProtocol {
    variable?: VariableADTvalue
}
export interface BackgroundProtocol
    extends BackgroundColorProtocol,
        BackgroundGradientProtocol,
        BackgroundImageProtocol,
        BackgroundVariableProtocol {
    type: BackgroundType
}

export interface BackgroundNoImageProtocol extends BackgroundColorProtocol, BackgroundGradientProtocol, BackgroundVariableProtocol {
    type: Exclude<BackgroundType, 'image'>
}

// 四个边/角的设置
export type EdgeValue = [number | undefined, number | undefined, number | undefined, number | undefined]
export const EDGE_MODE = Enum('all', 'each')
export type EdgeMode = Enum<typeof EDGE_MODE>
export type EdgeConfig = {
    mode?: EdgeMode
    all?: number
    each?: EdgeValue
}

/** 纹理 */
export type Veins = {
    value?: string
    color?: string
    strokeWidth?: number
    size?: number
}

/** 边框 */
export type Border = BackgroundNoImageProtocol & EdgeConfig

/** 阴影 */
export type Shadow = {
    color?: string
    x?: number
    y?: number
    blur?: number
    diffusion?: number
}

export interface FontStyle extends TextProtocol {
    advance?: string
    textBorder: Border
    align?: AlignMent
}

export const DESIGN_THEME = Enum('LIGHT', 'DARK', 'CUSTOM')

export type DesignThemeType = Enum<typeof DESIGN_THEME>
export interface DesignStyle {
    theme?: DesignThemeType
    // 标题颜色
    labelColor?: BackgroundNoImageProtocol

    // 文本颜色
    color?: BackgroundNoImageProtocol

    // 图标颜色
    iconColor?: BackgroundNoImageProtocol

    // 占位符颜色
    placeholderColor?: BackgroundNoImageProtocol

    // 圆角
    radius?: EdgeValue

    // 背景
    background?: BackgroundProtocol
    // 文本边框
    // textBorder?: Border

    // 纹理
    veins?: Veins

    // 边框
    border?: Border

    // 阴影
    shadow?: Shadow

    // 模糊
    blur?: number

    // 标签栏线的颜色
    lineColor?: BackgroundNoImageProtocol

    // checkbox
    checkedColor?: BackgroundNoImageProtocol

    textBorder?: Border
}

export type RectSize = number | 'auto' | 'fill'

export type RectSizeUnit = '%' | 'px' | 'vw' | 'vh' | 'rem'

export type LayoutPositionType = 'relative' | 'absolute' | 'fixed' | 'sticky'
export interface LayoutPosition {
    type?: LayoutPositionType
    location?: {
        horizontal?: 'left' | 'right'
        vertical?: 'top' | 'bottom'
        horizontalValue?: number
        verticalValue?: number
    }
    zIndex?: number
}

export interface SizeConfigure {
    size: RectSize | undefined
    unit?: RectSizeUnit
    variable?: VariableADTvalue
}

export interface LayoutSize {
    /**
     * 宽度
     */
    width: SizeConfigure
    /**
     * 高度
     */
    height: SizeConfigure
    /**
     * 最小宽度
     */
    minWidth?: Partial<SizeConfigure>
    /**
     * 最小高度
     */
    minHeight?: Partial<SizeConfigure>
    /**
     * 最大宽度
     */
    maxWidth?: Partial<SizeConfigure>
    /**
     * 最大高度
     */
    maxHeight?: Partial<SizeConfigure>

    /**
     * 当内容超出
     */
    overflow?: 'hidden' | 'auto' | 'visible'
}

export interface AutoLayout {
    /**
     * 对齐相关
     */
    align?: {
        /**
         * 方向
         */
        direction?: Direction
        /**
         * 是否分布对齐
         */
        isDistributedAlignment?: boolean
        /**
         * 对齐方式
         */
        alignX?: FlexAlign
        alignY?: FlexAlign
    }

    /**
     * 间距
     */
    gap?: { size?: number; unit?: Exclude<RectSizeUnit, 'vw' | 'vh'> }
    /**
     * 内边距
     */
    padding?: { size?: EdgeValue; unit?: Exclude<RectSizeUnit, 'vw' | 'vh'> }

    wrap?: boolean
}

export interface SliderDesign {
    rangeBackground?: BackgroundNoImageProtocol
    background?: BackgroundProtocol
}
// export interface HoverStyle {
//     textColor: string
//     iconColor: string
//     placeholderColor: string
//     radius: EdgeValue
//     background: BackgroundProtocol
//     border: Border
//     shadow: Shadow
// }

export interface CursorProtocol {
    enable?: boolean
    type: 'custom' | 'system'
    value?: string
    url?: string
}
